import { Helmet } from 'react-helmet-async'
import { Link } from "../navigation"
import { useTranslation } from 'react-i18next'
import _, { Dictionary } from 'lodash'

import { CompetitorFamily, CompetitorMember } from './external'
import { competitorFamilyUrl, competitorMemberUrl } from './utils'
import { useExternal } from './'
import { SortButton } from '../patents/datawizard/DataWizard'
import { useFilteredExternal } from '../filter/FilteredExternal'
import { createExcelFile } from '../backend'
import { useAuth } from '../user/Auth'
import { useProducts } from '../products/ProductsProvider'
import { Commodity } from '../products/products'
import { useInventions, Invention } from '../inventions/InventionsProvider'
import LoadingButton from '../components/button/LoadingButton'
import TagList from '../components/TagList'
import { DwSettingsProvider, useDwSettingsProvider } from 'src/patents/datawizard/DwSettings'
import { QueriesProvider, QueryDeleteModal, QueryEditorModal, QueryMenu, QueryOpenModal, useQueriesProvider } from 'src/patents/datawizard/Queries'
import { competitor_member } from 'src/data'

// Group-by values
const byCompetitorMember = 'competitor-member'
const byCompetitorFamily = 'family'
const byProduct = 'by-product'
const byInvention = 'by-invention'

// Field-group keys (distinct from the group-by values above)
const productFields = 'product'
const inventionFields = 'invention'

// Family first, then member, then product, then invention — matching the Patents data wizard order (#8)
const groupByFields = [byCompetitorFamily, byCompetitorMember, byProduct, byInvention]

type DwRow = { members: CompetitorMember[], families: CompetitorFamily[], products: Commodity[], inventions: Invention[], id: number }

function useRows(groupBy: string): DwRow[] {
    const { members, families, familyById, membersByFamilyId, memberById } = useFilteredExternal()
    const { commodities, commodityById } = useProducts()
    const { competitorCommodityLinks } = useExternal()
    const { inventions } = useInventions()

    const commoditiesByFamilyId = _(competitorCommodityLinks)
        .groupBy(l => l.competitorFamilyId)
        .mapValues(ls => ls.map(l => commodityById[l.commodityId]).filter(Boolean))
        .value()
    const inventionsByCompetitorMemberId = _(inventions)
        .filter(i => i.competitorMemberId != null)
        .groupBy(i => i.competitorMemberId)
        .value()
    const inventionsOf = (mems: CompetitorMember[]) =>
        _(mems).flatMap(m => inventionsByCompetitorMemberId[m.competitorMemberId] ?? []).uniqBy('inventionId').value()

    switch (groupBy) {
        case byCompetitorMember:
            return members.map(m => ({
                id: m.competitorMemberId,
                members: [m],
                families: [familyById[m.competitorFamilyId]].filter(Boolean),
                products: commoditiesByFamilyId[m.competitorFamilyId] ?? [],
                inventions: inventionsByCompetitorMemberId[m.competitorMemberId] ?? [],
            }))
        case byCompetitorFamily:
            return families.map(family => {
                const mems = membersByFamilyId[family.competitorFamilyId] ?? []
                return {
                    id: family.competitorFamilyId,
                    members: mems,
                    families: [family],
                    products: commoditiesByFamilyId[family.competitorFamilyId] ?? [],
                    inventions: inventionsOf(mems),
                }
            })
        case byProduct:
            return commodities.map(c => {
                const familyIds = _(competitorCommodityLinks)
                    .filter(l => l.commodityId === c.commodityId)
                    .map(l => l.competitorFamilyId)
                    .uniq()
                    .value()
                const fams = familyIds.map(id => familyById[id]).filter(Boolean)
                const mems = fams.flatMap(f => membersByFamilyId[f.competitorFamilyId] ?? [])
                return { id: c.commodityId, families: fams, members: mems, products: [c], inventions: inventionsOf(mems) }
            })
        case byInvention:
            return _(inventions)
                .filter(i => i.competitorMemberId != null)
                .map(i => {
                    const m = memberById[i.competitorMemberId]
                    const mems = m ? [m] : []
                    const fams = m ? [familyById[m.competitorFamilyId]].filter(Boolean) : []
                    return {
                        id: i.inventionId,
                        members: mems,
                        families: fams,
                        products: m ? (commoditiesByFamilyId[m.competitorFamilyId] ?? []) : [],
                        inventions: [i],
                    }
                })
                .value()
        default:
            console.error("Unknown group by ", groupBy)
            return []
    }
}

// Mirrors the Patents data wizard: identifier/date fields are only counted (how many cells
// are populated), categorical fields show a value breakdown, and claim counts are summed.
const countOnlyFields = new Set([
    'internalReference', 'externalReference',
    'applicationNumber', 'publicationNumber', 'patentNumber', 'expiryDate',
    'familyReference', 'familyName', 'familyExtReference', 'summary', 'priorityDate',
    'commodityReference', 'commodityDescription',
    'inventionReference', 'inventionTitle',
])

function dotProduct(values: Dictionary<number>) {
    return _(values).entries().map(([k, v]) => (+k) * v).sum()
}

export default function ExternalDatawizard() {
    return (
        <DwSettingsProvider entity={competitor_member} defaultGroupBy={byCompetitorMember} allowedValues={groupByFields}>
            <QueriesProvider entity={competitor_member}>
                <DataWizardView />
            </QueriesProvider>
        </DwSettingsProvider>
    )
}

function DataWizardView() {
    const { t } = useTranslation()
    const { team } = useAuth()
    const {
        groupBy, setGroupBy,
        searchFields, setSearchFields,
        sortField, setSortField,
        sortOrder, setSortOrder,
    } = useDwSettingsProvider()

    const { showSaveAsModal, showDeleteModal, showOpenModal, selectedQuery } = useQueriesProvider()

    const _searchableFields = {
        // Family group first (#8)
        [byCompetitorFamily]: {
            familyReference: (f: CompetitorFamily) => f.internalReference,
            familyName: (f: CompetitorFamily) => f.familyName,
            familyExtReference: (f: CompetitorFamily) => f.externalReference,
            summary: (f: CompetitorFamily) => f.summary,
            priorityDate: (f: CompetitorFamily) => f.priorityDate,
        },
        [byCompetitorMember]: {
            internalReference: (m: CompetitorMember) => m.internalReference,
            countryCode: (m: CompetitorMember) => m.countryCode,
            status: (m: CompetitorMember) => t(m.competitorMemberStatus),
            ipType: (m: CompetitorMember) => m.ipType ? t(m.ipType) : undefined,
            applicationNumber: (m: CompetitorMember) => m.applicationNumber,
            publicationNumber: (m: CompetitorMember) => m.publicationNumber,
            patentNumber: (m: CompetitorMember) => m.patentNumber,
            expiryDate: (m: CompetitorMember) => m.expiryDate,
            numberClaims: (m: CompetitorMember) => m.numberClaims != null ? String(m.numberClaims) : undefined,
            externalReference: (m: CompetitorMember) => m.externalReference,
        },
        // Product group (#5)
        [productFields]: {
            commodityReference: (c: Commodity) => c.commodityReference,
            commodityClass: (c: Commodity) => c.commodityClass,
            commodityDescription: (c: Commodity) => c.commodityDescription,
        },
        // Invention group (#6)
        [inventionFields]: {
            inventionReference: (i: Invention) => i.reference,
            inventionTitle: (i: Invention) => i.title,
        },
    }

    const searchableFields = _(_searchableFields).toPairs()
        .flatMap(([_group, fields]) => _(fields).toPairs().filter(([_field, fn]) => fn !== undefined).value())
        .fromPairs()
        .value()
    const validSearchFields = Object.keys(searchableFields)

    const sortIndex = searchFields.indexOf(sortField)
    const collator = new Intl.Collator()

    const displayFields = Object.fromEntries([
        ..._(searchableFields).toPairs().map(([_group, fields]) => [_group, ..._.keys(fields)]).value()
    ].map(f => [f, t(f)]))

    const rows = useRows(groupBy)

    const rowData = _(rows)
        .map(row => ({
            ...row,
            columns: searchFields
                .filter(f => validSearchFields.includes(f))
                .map(field => {
                    let values: (string | undefined)[] = []
                    if (field in _searchableFields[byCompetitorMember]) {
                        values = _(row.members).flatMap(_searchableFields[byCompetitorMember][field]).value()
                    } else if (field in _searchableFields[byCompetitorFamily]) {
                        values = _(row.families).flatMap(_searchableFields[byCompetitorFamily][field]).value()
                    } else if (field in _searchableFields[productFields]) {
                        values = _(row.products).flatMap(_searchableFields[productFields][field]).value()
                    } else if (field in _searchableFields[inventionFields]) {
                        values = _(row.inventions).flatMap(_searchableFields[inventionFields][field]).value()
                    }
                    return _(values).filter(v => v !== undefined && v !== '').countBy().value()
                })
        }))
        .sort((rowA, rowB) => {
            if (sortIndex < 0) return 0
            const a = Object.keys(rowA.columns[sortIndex]).sort()?.[0]
            const b = Object.keys(rowB.columns[sortIndex]).sort()?.[0]
            return a === undefined ? 1 : b === undefined ? -1 : (collator.compare(a, b) * sortOrder)
        })
        // "No values": hide rows where every column is empty, like the Patents wizard
        .filter(({ columns }) => columns.some(c => _.size(c) > 0))
        .value()

    // Richer totals, matching Patents: count for identifiers/dates, weighted sum for claim
    // counts, and a value breakdown for everything else (e.g. countries, status, ip type).
    const totalRow: Record<string, Dictionary<number>> = Object.fromEntries(
        searchFields.map((field, fi) =>
            countOnlyFields.has(field)
                ? [field, { Total: _.sum(rowData.map(r => _.size(r.columns[fi]))) }]
                : field === 'numberClaims'
                    ? [field, { Total: _(rowData).map(r => dotProduct(r.columns[fi])).sum() }]
                    : [field, _(rowData)
                        .flatMap(r => _.toPairs(r.columns[fi]))
                        .groupBy(([key]) => key)
                        .mapValues(vs => _.sum(vs.map(([, c]) => c)))
                        .value()]
        )
    )
    const tableData = { rows: rowData, columnKeys: searchFields, columns: searchFields.map(t) }

    async function exportTable() {
        const excelFile = {
            sheets: [
                {
                    name: t('external'),
                    header: tableData.columns,
                    rows: tableData.rows.map(row => row.columns.map(values => Object.keys(values).sort().join(', '))),
                    footer: tableData.columnKeys.map(k => asStrings(totalRow[k] ?? {}).join('\n'))
                }
            ]
        }
        return createExcelFile(excelFile, `monitoring-data-${team.replace(' ', '-')}-${new Date().toISOString()}.xlsx`)
    }

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{t('data-wizard')} | {t('external')} | Patent Cockpit</title>
        </Helmet>
        <div className="header-row">
            <div className="flex flex-row gap-2">
                <h2 className="modern-h2 grow">{t('data-wizard')}</h2>
                <LoadingButton onClick={exportTable} className="btn-secondary px-2 py-px font-normal text-sm w-fit">
                    {t('excel-export')}
                </LoadingButton>
            </div>
        </div>
        <div className='main-content'>
            <div className='flex flex-row gap-8'>
                <div className='grow grid grid-cols-[8rem_1fr] gap-2 items-center'>
                    <label htmlFor='group-by' className='label normal-case'>{t('group-by')}</label>
                    <select id='group-by' className='form-select py-px pl-2 w-fit' value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                        {groupByFields.map(field => <option key={field} value={field}>{t(field)}</option>)}
                    </select>

                    <label htmlFor='fields' className='label normal-case'>{t('fields')}</label>

                    <TagList {...{
                        name: "searchFields",
                        dragable: true,
                        availableTags: _(_searchableFields)
                            .toPairs()
                            .map(([k, vs]) => [t(k), _(vs).keys().filter(v => vs[v] !== undefined).sortBy(v => displayFields[v] ?? v).value()])
                            .fromPairs()
                            .value(),
                        tags: searchFields,
                        setTags: setSearchFields,
                        tagDisplays: displayFields,
                        placeholder: t('field'),
                    }} />
                </div>
                <div className="flex lg:flex-col sm:flex-row flex-col lg:items-end sm:items-center gap-2">
                    <h3 className="text-slate-600 whitespace-nowrap">{selectedQuery?.queryName ?? t('unnamed-query')}</h3>
                    <QueryMenu />
                </div>

                {showSaveAsModal && <QueryEditorModal />}
                {showDeleteModal && <QueryDeleteModal />}
                {showOpenModal && <QueryOpenModal />}
            </div>
            <table className='mt-8 border-spacing-0 border-collapse'>
                <thead className='text-left'>
                    <tr>
                        {searchFields.map(searchField =>
                            <th key={searchField} className='sticky bg-white dark:bg-pcx-100 font-semibold text-lg px-0 whitespace-nowrap'>
                                <div className='flex flex-row gap-2 items-center px-1 border-b-2 border-pcx-500 w-full'>
                                    {displayFields[searchField]}
                                    <SortButton {...{ searchField, sortField, setSortField, sortOrder, setSortOrder }} />
                                </div>
                            </th>)}
                    </tr>
                </thead>
                <tbody>
                    {tableData.rows.map(row =>
                        <tr key={row.id} className='even:bg-pcx-100 dark:even:bg-pcx-200 last:border-b-2 last:border-pcx-500'>
                            {row.columns.map((values, colIdx) =>
                                <td key={`${row.id}-${colIdx}`} className='px-1 align-top py-0.5'>
                                    {renderCell(values, searchFields[colIdx])}
                                </td>)}
                        </tr>)}
                </tbody>
                <tfoot>
                    <tr>
                        {searchFields.map((field) =>
                            <td key={field} className='px-1 py-0.5 align-top'>
                                <FooterCell values={totalRow[field]} />
                            </td>)}
                    </tr>
                </tfoot>
            </table>
        </div>
    </>
}

const linkStyle = "underline-link py-0 whitespace-nowrap after:content-[';'] mr-1 last:mr-0 last:after:content-['']"
function renderCell(values: Dictionary<number>, field: string) {
    if (field === 'internalReference') {
        return _.keys(values).sort().map(reference => (
            <Link key={reference} className={linkStyle} to={competitorMemberUrl({ internalReference: reference })}>
                {reference}
            </Link>
        ))
    } else if (field === 'familyReference') {
        return _.keys(values).sort().map(reference => (
            <Link key={reference} className={linkStyle} to={competitorFamilyUrl({ internalReference: reference })}>
                {reference}
            </Link>
        ))
    } else {
        return _.keys(values).sort().join(', ')
    }
}

function asStrings(values: Dictionary<number>) {
    return _(values)
        .toPairs()
        .sortBy([([_v, c]) => -c, ([v]) => v.toLowerCase()])
        .map(([value, count]) => `${value}: ${count}`)
        .value()
}

function FooterCell({ values }: { values: Dictionary<number> }) {
    return (
        <div className='flex flex-col'>
            {asStrings(values).map((s, i) => <span key={i}>{s}</span>)}
        </div>
    )
}
